import type { Registry as Services } from '@ember/service';
import { tKey } from 'ember-intl';

const AMOUNT_MORE_KEY = tKey('general.amount_more');

const MAX_NAMED_RATE_GROUPS = 2;

export interface RateGroupMinLos {
  hasOverride: boolean;
  hotelRestrictionApplies: boolean;
  isLoading: boolean;
  rateGroupId: string;
  value: number | null;
}

export interface RateGroupMinLosRow extends Omit<
  RateGroupMinLos,
  'rateGroupId'
> {
  rateGroupIds: string[];
}

/**
 * Collapses rate groups that would render an identical MinLOS row into one row.
 *
 * `hotelRestrictionApplies` and `hasOverride` are part of the merge key because they
 * change how the value is drawn: two groups both showing "MinLOS 2" must stay apart
 * when only one is struck through, or only one shows the override indicator — the
 * icon asserts "automation paused" for every group in the row, so OR-ing it like
 * `isLoading` would put a false claim on groups without an override.
 *
 * `isLoading` is not part of the key, so a merged row can cover both a settled group
 * and one mid-push. Such a row loads when any of its groups does, matching the single
 * rate group row it replaces: a skeleton never asserts a value that is about to change.
 *
 * RATEGROUP TODO (COA-4197): input order is preserved, so rows follow the order
 * configured rate groups arrive in; replace with the admin-configured ranking.
 */
export function groupRateGroupsByMinLos(
  entries: RateGroupMinLos[],
): RateGroupMinLosRow[] {
  const rows: RateGroupMinLosRow[] = [];

  for (const entry of entries) {
    const existingRow = rows.find(
      (row) =>
        row.value === entry.value &&
        row.hotelRestrictionApplies === entry.hotelRestrictionApplies &&
        row.hasOverride === entry.hasOverride,
    );

    if (existingRow) {
      existingRow.rateGroupIds.push(entry.rateGroupId);
      existingRow.isLoading = existingRow.isLoading || entry.isLoading;
      continue;
    }

    rows.push({
      hasOverride: entry.hasOverride,
      hotelRestrictionApplies: entry.hotelRestrictionApplies,
      isLoading: entry.isLoading,
      rateGroupIds: [entry.rateGroupId],
      value: entry.value,
    });
  }

  return rows;
}

/**
 * Labels a merged row, e.g. "Group 1, Group 2, 2 more...".
 *
 * Names are shown verbatim rather than translated: the hotel wrote them in CEMA.
 */
export function formatRateGroupLabel(
  rateGroupNames: string[],
  intl: Services['intl'],
): string {
  const labelParts = rateGroupNames.slice(0, MAX_NAMED_RATE_GROUPS);
  const remainingCount = rateGroupNames.length - labelParts.length;

  if (remainingCount > 0) {
    labelParts.push(intl.t(AMOUNT_MORE_KEY, { count: remainingCount }));
  }

  return labelParts.join(', ');
}
